import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, User, AtSign, FileText, Loader, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface EmailForm {
  name: string;
  email: string;
  message: string;
}

interface EmailChatTabProps {
  onClose?: () => void;
}

const EmailChatTab: React.FC<EmailChatTabProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailForm, setEmailForm] = useState<EmailForm>({
    name: '',
    email: '',
    message: ''
  });

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!emailForm.name || !emailForm.email || !emailForm.message) {
      toast.error(t('chat.email.validation_error', 'Compila tutti i campi richiesti'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Sending email via API:", { ...emailForm, language: i18n.language });
      
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language || 'it'
        },
        body: JSON.stringify({
          ...emailForm,
          language: i18n.language,
          to: "victor@victorlazari.com",
          subject: `${t('chat.email.subject', 'Nuovo Messaggio dal Sito Web')} - ${emailForm.name}`,
          replyTo: emailForm.email
        })
      });
      
      const result = await response.json();
      console.log("Email API response:", result);
      
      if (result.success) {
        toast.success(t('chat.email.success', 'Messaggio inviato con successo'));
        setEmailForm({
          name: '',
          email: '',
          message: ''
        });
        if (onClose) {
          setTimeout(onClose, 2000);
        }
      } else {
        toast.error(result.message || t('chat.email.error', 'Errore nell\'invio del messaggio'));
        console.error('Email sending failed:', result);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(t('chat.email.error', 'Errore nell\'invio del messaggio'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <form onSubmit={handleEmailSubmit} className="flex flex-col h-full p-4 bg-white dark:bg-gray-800">
      
      <div className="mb-4 text-center">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100 mb-2">
          {t('chat.modes.email.title')}
        </h3>
        <p className="text-slate-600 dark:text-gray-300 text-sm">
          {t('chat.modes.email.description')}
        </p>
      </div>
      
      <div className="space-y-5 flex-1 overflow-y-auto pb-4 px-1">
        <div className="relative">
          <label className="block text-slate-700 dark:text-gray-300 text-sm mb-2">
            <User className="w-4 h-4 inline mr-1" />
            {t('chat.modes.email.form.name')}
          </label>
          <input
            type="text"
            name="name"
            value={emailForm.name}
            onChange={handleEmailChange}
            required
            className="w-full bg-white dark:bg-gray-700/50 border border-slate-300 dark:border-gray-600 rounded-md px-3 py-2.5 text-slate-900 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-sky-600 dark:focus:ring-sky-500 focus:border-sky-600 dark:focus:border-sky-500"
          />
        </div>
        
        <div className="relative">
          <label className="block text-slate-700 dark:text-gray-300 text-sm mb-2">
            <AtSign className="w-4 h-4 inline mr-1" />
            {t('chat.modes.email.form.email')}
          </label>
          <input
            type="email"
            name="email"
            value={emailForm.email}
            onChange={handleEmailChange}
            required
            className="w-full bg-white dark:bg-gray-700/50 border border-slate-300 dark:border-gray-600 rounded-md px-3 py-2.5 text-slate-900 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-sky-600 dark:focus:ring-sky-500 focus:border-sky-600 dark:focus:border-sky-500"
          />
        </div>
        
        <div className="relative">
          <label className="block text-slate-700 dark:text-gray-300 text-sm mb-2">
            <FileText className="w-4 h-4 inline mr-1" />
            {t('chat.modes.email.form.message')}
          </label>
          <textarea
            name="message"
            value={emailForm.message}
            onChange={handleEmailChange}
            required
            rows={5}
            className="w-full bg-white dark:bg-gray-700/50 border border-slate-300 dark:border-gray-600 rounded-md px-3 py-2.5 text-slate-900 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-400 resize-none focus:outline-none focus:ring-1 focus:ring-sky-600 dark:focus:ring-sky-500 focus:border-sky-600 dark:focus:border-sky-500"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end relative">
        <button
          type="submit"
          className="p-3 bg-sky-600 dark:bg-sky-700 rounded text-white transition-all focus:outline-none focus:ring-1 focus:ring-sky-600/30 disabled:opacity-50 disabled:pointer-events-none self-end shadow-md min-w-[50px] min-h-[50px] flex items-center justify-center hover:bg-sky-700 dark:hover:bg-sky-600"
          disabled={!emailForm.message.trim() || isSubmitting}
          style={{}}
        >
          {isSubmitting ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Send className="w-6 h-6 text-white" />
          )}
        </button>
      </div>
    </form>
  );
};

export default EmailChatTab; 